import { sdk } from '@needful-things-mvp/sdk';

import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { type Paginated, delay, paginate } from './mock';
import { pushActivity } from './notifications';

export type MemberRole = 'admin' | 'user';
export type MemberStatus = 'active' | 'inactive' | 'banned';

export interface Member {
  id: string;
  name: string | null;
  email: string;
  picture: string | null;
  role: MemberRole;
  status: MemberStatus;
}

export interface MemberInput {
  email: string;
  name?: string;
  picture?: string;
  role?: MemberRole;
}

export interface ListMembersParams {
  search?: string;
  role?: MemberRole;
  page?: number;
  pageSize?: number;
}

export type PaginatedMembers = Paginated<Member>;

export const MEMBER_PAGE_SIZE = 50;

let db: Member[] = [
  {
    id: 'ana',
    name: 'Ana Souza',
    email: 'ana@needfulthings.com',
    picture: null,
    role: 'admin',
    status: 'active',
  },
  {
    id: 'marc',
    name: 'Marc Reid',
    email: 'marc@needfulthings.com',
    picture: null,
    role: 'admin',
    status: 'active',
  },
  {
    id: 'sofia',
    name: 'Sofia Rossi',
    email: 'sofia@needfulthings.com',
    picture: null,
    role: 'admin',
    status: 'inactive',
  },
];

function filter(term?: string): Member[] {
  const query = term?.trim().toLowerCase();
  if (!query) return db;
  return db.filter(
    (member) =>
      member.email.toLowerCase().includes(query) ||
      (member.name ?? '').toLowerCase().includes(query),
  );
}

/** `GET /admin/users`. */
export async function listMembers(params: ListMembersParams = {}): Promise<PaginatedMembers> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.users.get({
      query: {
        search: params.search,
        role: params.role,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    return unwrap<PaginatedMembers>(response);
  }
  await delay();
  const { search, page, pageSize = MEMBER_PAGE_SIZE } = params;
  return paginate(filter(search), page, pageSize);
}

/** `GET /admin/users/:id`. */
export async function getMember(id: string): Promise<Member | undefined> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.users({ id }).get();
    return unwrap<Member>(response);
  }
  await delay();
  return db.find((member) => member.id === id);
}

/** `POST /admin/users` (new members adopt this account on first login). */
export async function createMember(input: MemberInput): Promise<Member> {
  if (!USE_MOCK) {
    return unwrap<Member>(await sdk.api.admin.users.post(input));
  }
  await delay();
  if (db.some((member) => member.email === input.email)) {
    throw new Error('A user with this email already exists');
  }

  const member: Member = {
    id: crypto.randomUUID(),
    name: input.name ?? null,
    email: input.email,
    picture: input.picture ?? null,
    role: input.role ?? 'admin',
    status: 'active',
  };
  db = [member, ...db];
  await pushActivity({
    verb: 'added',
    entity: 'member',
    targetType: 'member',
    targetId: member.id,
    targetLabel: member.name ?? member.email,
  });
  return member;
}

/** `PATCH /admin/users/:id`. */
export async function updateMember(
  id: string,
  patch: Partial<Pick<Member, 'role' | 'status'>>,
): Promise<Member> {
  if (!USE_MOCK) {
    return unwrap<Member>(await sdk.api.admin.users({ id }).patch(patch));
  }
  await delay();
  let updated: Member | undefined;
  db = db.map((member) => {
    if (member.id !== id) return member;
    updated = { ...member, ...patch };
    return updated;
  });
  if (!updated) throw new Error(`Member ${id} not found`);
  await pushActivity({
    verb: 'updated',
    entity: 'member',
    targetType: 'member',
    targetId: updated.id,
    targetLabel: updated.name ?? updated.email,
  });
  return updated;
}
